import { apiFetch, readApiJson } from '@/lib/api-fetch'
import { OperationSchema, type Operation } from '@/lib/api/schemas/operations.schema'
import {
  YmOfferPricingDetailSchema,
  YmOfferPricingPageSchema,
  YmPricingApplyRequestSchema,
  YmPricingPreviewRequestSchema,
  YmPricingPreviewSchema,
  YmQuarantineConfirmRequestSchema,
  type YmDriftState,
  type YmOfferPricing,
  type YmOfferPricingDetail,
  type YmOfferPricingPage,
  type YmPricingPreview,
  type YmPricingPreviewRequest,
  type YmQuarantineConfirmRequest,
  YmEconomicsStatsSchema,
  YmEconomicsStatsRequestSchema,
  type YmEconomicsStats,
  type YmEconomicsStatsRequest,
  type YmEconomicsFilter,
  YmEconomicsPageSchema,
  type YmEconomicsPage,
  YmEconomicsPieceSchema,
  type YmEconomicsPiece,
  YmEconomicsBulkPreviewRequestSchema,
  YmEconomicsBulkPreviewSchema,
  type YmEconomicsBulkPreview,
  type YmEconomicsBulkPreviewRequest,
  FormulaRefusalSchema,
  YmShowcaseFormulaBodySchema,
  YmShowcaseFormulaPreviewBodySchema,
  YmShowcaseFormulaPreviewSchema,
  YmShowcaseFormulaSchema,
  type YmShowcaseFormula,
  type YmShowcaseFormulaBody,
  type YmShowcaseFormulaPreview,
  type YmShowcaseFormulaPreviewBody,
} from '@/lib/api/schemas/ymarket-pricing.schema'

const BASE = '/api/ymarket-pricing'

export type YmOfferListQuery = {
  drift?: YmDriftState | null
  q?: string
  limit?: number
  cursor?: string | null
}

function postJson(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
  return apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
}

export async function listYmOfferPricing(
  { drift, q, limit, cursor }: YmOfferListQuery = {},
  signal?: AbortSignal,
): Promise<YmOfferPricingPage> {
  const query = new URLSearchParams()
  if (drift) query.set('drift', drift)
  const search = q?.trim()
  if (search) query.set('q', search)
  if (limit !== undefined) query.set('limit', String(limit))
  if (cursor) query.set('cursor', cursor)
  const encoded = query.toString()
  const suffix = encoded.length > 0 ? `?${encoded}` : ''
  return YmOfferPricingPageSchema.parse(await readApiJson(await apiFetch(`${BASE}/offers${suffix}`, { signal })))
}

const OFFER_PAGE_LIMIT = 200
const OFFER_PAGE_CAP = 25

/** Walks every page of published offers; bounded so a runaway cursor cannot loop forever. */
export async function listAllYmOfferPricing(signal?: AbortSignal): Promise<YmOfferPricing[]> {
  const offers: YmOfferPricing[] = []
  let cursor: string | null = null
  for (let page = 0; page < OFFER_PAGE_CAP; page += 1) {
    const result: YmOfferPricingPage = await listYmOfferPricing({ limit: OFFER_PAGE_LIMIT, cursor }, signal)
    offers.push(...result.items)
    cursor = result.nextCursor
    if (cursor === null) break
  }
  return offers
}

export async function getYmOfferPricing(offerId: string, signal?: AbortSignal): Promise<YmOfferPricingDetail> {
  const response = await apiFetch(`${BASE}/offers/${encodeURIComponent(offerId)}`, { signal })
  return YmOfferPricingDetailSchema.parse(await readApiJson(response))
}

export async function collectYmPrices(): Promise<Operation> {
  return OperationSchema.parse(await readApiJson(await postJson(`${BASE}/collect`, {})))
}

export async function previewYmPricing(request: YmPricingPreviewRequest, signal?: AbortSignal): Promise<YmPricingPreview> {
  const response = await postJson(`${BASE}/preview`, YmPricingPreviewRequestSchema.parse(request), signal)
  return YmPricingPreviewSchema.parse(await readApiJson(response))
}

export async function applyYmPricing(previewHash: string): Promise<Operation> {
  const response = await postJson(`${BASE}/apply`, YmPricingApplyRequestSchema.parse({ previewHash }))
  return OperationSchema.parse(await readApiJson(response))
}

export async function confirmYmQuarantine(request: YmQuarantineConfirmRequest): Promise<Operation> {
  const response = await postJson(`${BASE}/quarantine/confirm`, YmQuarantineConfirmRequestSchema.parse(request))
  return OperationSchema.parse(await readApiJson(response))
}

export async function getYmEconomicsStats(
  request: YmEconomicsStatsRequest,
  signal?: AbortSignal,
): Promise<YmEconomicsStats> {
  const response = await postJson(`${BASE}/economics/stats`, YmEconomicsStatsRequestSchema.parse(request), signal)
  return YmEconomicsStatsSchema.parse(await readApiJson(response))
}

/** One server page of the stored projection; the server computes only what the page needs. */
export async function listYmEconomics(
  { filter, q, limit, cursor }: { filter?: YmEconomicsFilter; q?: string; limit?: number; cursor?: string } = {},
  signal?: AbortSignal,
): Promise<YmEconomicsPage> {
  const query = new URLSearchParams()
  if (filter) query.set('filter', filter)
  if (q?.trim()) query.set('q', q.trim())
  if (limit !== undefined) query.set('limit', String(limit))
  if (cursor) query.set('cursor', cursor)
  const suffix = query.toString()
  const response = await apiFetch(`${BASE}/economics${suffix ? `?${suffix}` : ''}`, { signal })
  return YmEconomicsPageSchema.parse(await readApiJson(response))
}

export async function getYmEconomicsPiece(productId: string, signal?: AbortSignal): Promise<YmEconomicsPiece> {
  const response = await apiFetch(`${BASE}/economics/${encodeURIComponent(productId)}`, { signal })
  return YmEconomicsPieceSchema.parse(await readApiJson(response))
}

export async function previewYmEconomicsBulk(
  request: YmEconomicsBulkPreviewRequest,
  signal?: AbortSignal,
): Promise<YmEconomicsBulkPreview> {
  const response = await postJson(`${BASE}/economics/bulk/preview`, YmEconomicsBulkPreviewRequestSchema.parse(request), signal)
  return YmEconomicsBulkPreviewSchema.parse(await readApiJson(response))
}

/** Queues the reviewed preview as ONE operation; progress lives in the operations bubble. */
export async function applyYmEconomicsBulk(
  previewHash: string,
  overrides?: Record<string, number>,
): Promise<Operation> {
  const response = await postJson(`${BASE}/economics/bulk/apply`, YmPricingApplyRequestSchema.parse({
    previewHash,
    ...(overrides === undefined ? {} : { overrides }),
  }))
  return OperationSchema.parse(await readApiJson(response))
}

/** A formula the server refused: which formula, the 1-based character and the Russian sentence. */
export class FormulaRefusalError extends Error {
  readonly key: string | null
  readonly position: number | null
  readonly code: string

  constructor(code: string, message: string, key: string | null, position: number | null) {
    super(message)
    this.name = 'FormulaRefusalError'
    this.code = code
    this.key = key
    this.position = position
  }
}

async function readFormulaJson(response: Response): Promise<unknown> {
  if (response.status === 422) {
    const body: unknown = await response.clone().json().catch(() => null)
    const parsed = FormulaRefusalSchema.safeParse(body)
    if (parsed.success) {
      const { code, message, key, position } = parsed.data.detail
      throw new FormulaRefusalError(code, message, key, position)
    }
  }
  return readApiJson(response)
}

/** The showcase, Yandex Pay and in-hand formulas in force, their reference and history. */
export async function getShowcaseFormula(signal?: AbortSignal): Promise<YmShowcaseFormula> {
  const response = await apiFetch(`${BASE}/showcase-formula`, { signal })
  return YmShowcaseFormulaSchema.parse(await readApiJson(response))
}

/** Saves changed formulas as a new version; every reader uses them on its next request. */
export async function putShowcaseFormula(body: YmShowcaseFormulaBody): Promise<YmShowcaseFormula> {
  const response = await apiFetch(`${BASE}/showcase-formula`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(YmShowcaseFormulaBodySchema.parse(body)),
  })
  return YmShowcaseFormulaSchema.parse(await readFormulaJson(response))
}

/** Evaluates candidate formulas beside the ones in force; writes nothing. */
export async function previewShowcaseFormula(
  body: YmShowcaseFormulaPreviewBody,
  signal?: AbortSignal,
): Promise<YmShowcaseFormulaPreview> {
  const response = await postJson(`${BASE}/showcase-formula/preview`, YmShowcaseFormulaPreviewBodySchema.parse(body), signal)
  return YmShowcaseFormulaPreviewSchema.parse(await readFormulaJson(response))
}
